import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { MapPin, Store, ArrowRight } from 'lucide-react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { SEO, breadcrumbJsonLd } from '@/components/SEO';
import { neutralProviderName } from '@/lib/providerName';
import { absoluteUrl, currentUrlLang, pathForLang } from '@/i18n/urlLanguage';
import { SURFACES, localFaq } from '@/content/faqSource';

/**
 * Local SEO page « location de scooter à Patong » (/location-scooter-patong,
 * /en/scooter-rental-patong): the real public scooters of Patong
 * (GET /api/seo/local/scooters-patong, same list as the HTML served before
 * JavaScript by functions/_lib/localPage.js), practical information and FAQ.
 * Not indexed while it lists too few real offers (`indexable` from the API).
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_ID = 'scooters-patong';
const FR_PATH = '/location-scooter-patong';

export const LocalScooterPatong = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const lang = currentUrlLang() || 'fr';
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  // One slug per language: the other language's slug opens the right page.
  useEffect(() => {
    const expected = pathForLang(lang, FR_PATH);
    const current = `${lang === 'en' ? '/en' : ''}${location.pathname === '/' ? '' : location.pathname}`;
    if (current.replace(/\/+$/, '') !== expected) window.location.replace(expected);
  }, [lang, location.pathname]);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/seo/local/${PAGE_ID}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, []);

  const k = (key, opts) => t(`localPages.scooterPatong.${key}`, opts);
  const sections = k('sections', { returnObjects: true });
  const faq = localFaq(SURFACES.scooterPatong, lang);     // same questions and answers as /faq (single source)
  const listings = data?.listings || [];
  const number = (n) => Number(n).toLocaleString(i18n.language === 'en' ? 'en-US' : 'fr-FR');
  const pageUrl = absoluteUrl(lang, FR_PATH);

  const jsonLd = [
    breadcrumbJsonLd([
      { name: t('seo.breadcrumb.home'), path: '/' },
      { name: t('seo.breadcrumb.scooters'), path: '/scooters' },
      { name: k('breadcrumb'), path: FR_PATH },
    ]),
    ...(listings.length ? [{
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: k('offersTitle'),
      numberOfItems: listings.length,
      itemListElement: listings.map((r, i) => ({
        '@type': 'ListItem', position: i + 1, name: r.title, url: absoluteUrl(lang, r.path),
      })),
    }] : []),
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faq.map((f) => ({
        '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden" data-testid="local-scooter-patong">
      <SEO
        title={k('seoTitle')}
        description={k('seoDescription')}
        canonical={pageUrl}
        noindex={Boolean(data) && !data.indexable}
        jsonLd={jsonLd}
      />
      <Header />

      <main className="pt-24 pb-16">
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-sm text-ink-light mb-4 flex flex-wrap items-center gap-1" aria-label="breadcrumb">
            <Link to="/" className="hover:text-accent">{t('seo.breadcrumb.home')}</Link>
            <span aria-hidden="true">›</span>
            <Link to="/scooters" className="hover:text-accent">{t('seo.breadcrumb.scooters')}</Link>
            <span aria-hidden="true">›</span>
            <span className="text-primary font-medium">{k('breadcrumb')}</span>
          </nav>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary mb-5 break-words" data-testid="local-h1">
            {k('h1')}
          </h1>
          <div className="max-w-3xl space-y-3 text-base sm:text-lg text-ink-light leading-relaxed">
            <p>{k('intro1')}</p>
            <p>{k('intro2')}</p>
          </div>
          {data && data.count > 0 && (
            <p className="mt-5 inline-flex items-start gap-2 rounded-2xl bg-accent/10 text-primary text-sm font-medium px-4 py-2" data-testid="local-stats">
              <MapPin className="w-4 h-4 mt-0.5 text-accent shrink-0" />
              <span>{k('stats', { count: data.count, providers: data.providers, min: number(data.price_min), max: number(data.price_max) })}</span>
            </p>
          )}
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10" id="offers">
          <h2 className="text-2xl font-semibold text-primary mb-5">{k('offersTitle')}</h2>
          {!data && !failed && <p className="text-ink-light" data-testid="local-loading">{k('loading')}</p>}
          {(failed || (data && listings.length === 0)) && (
            <div className="rounded-2xl border border-slate-200 p-6 text-center" data-testid="local-empty">
              <p className="text-ink-light mb-4">{k('empty')}</p>
              <Link to="/scooters" className="btn-primary inline-flex items-center gap-2">{k('seeAll')} <ArrowRight className="w-4 h-4" /></Link>
            </div>
          )}
          {listings.length > 0 && (
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="local-offers">
              {listings.map((r) => (
                <li key={r.service_id} className="card-offer overflow-hidden flex flex-col" data-testid={`local-offer-${r.service_id}`}>
                  <Link to={r.path} className="block relative h-52 bg-slate-100 overflow-hidden" tabIndex={-1} aria-hidden="true">
                    {r.image && (
                      <img src={r.image} alt="" width="640" height="416" loading="lazy" className="w-full h-full object-cover" />
                    )}
                  </Link>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="text-lg font-semibold text-primary leading-tight break-words">{r.title}</h3>
                    <p className="mt-1 text-sm text-ink-light flex items-center gap-1 min-w-0">
                      <Store className="w-4 h-4 text-accent shrink-0" />
                      <span className="truncate">{k('provider', { name: r.provider_name || neutralProviderName() })}</span>
                    </p>
                    <div className="mt-auto pt-4 flex flex-wrap items-end justify-between gap-3 border-t border-slate-100">
                      <p className="flex items-baseline gap-1" data-testid={`local-price-${r.service_id}`}>
                        <span className="text-2xl font-bold text-primary">{number(r.price)}</span>
                        <span className="text-sm text-ink-light">{k('perDay')}</span>
                      </p>
                      <Link to={r.path} className="btn-primary text-sm inline-flex items-center gap-1" data-testid={`local-view-${r.service_id}`}>
                        {k('viewListing')} <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {(Array.isArray(sections) ? sections : []).map((s) => (
            <div key={s.title} className="rounded-2xl bg-background-soft p-6">
              <h2 className="text-lg font-semibold text-primary mb-2">{s.title}</h2>
              <p className="text-sm text-ink-light leading-relaxed">{s.text}</p>
            </div>
          ))}
        </section>

        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-14" data-testid="local-faq">
          <h2 className="text-2xl font-semibold text-primary mb-5">{k('faqTitle')}</h2>
          <div className="divide-y divide-slate-200 border-y border-slate-200">
            {faq.map((f) => (
              <details key={f.id} className="py-4 group">
                <summary className="cursor-pointer font-medium text-primary list-none flex justify-between gap-4">
                  <span>{f.q}</span><span className="text-accent group-open:rotate-45 transition-transform" aria-hidden="true">+</span>
                </summary>
                <p className="mt-3 text-sm text-ink-light leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/scooters" className="btn-primary inline-flex items-center gap-2" data-testid="local-see-all">{k('seeAll')} <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default LocalScooterPatong;
